import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { dueDateSortKey, extractInvoices } from '../lib/payments';
import type { PaymentAttempt, PayableInvoice } from '../types/payments';
import { useAttemptPoller } from './useAttemptPoller';

/** One invoice paid during a run — what the progress and summary panels need. */
export interface RunSuccess {
  invoice: PayableInvoice;
  attemptId: string;
  amount: number;
  currency: string;
}

export type PaymentRunStatus =
  | 'idle'
  | 'snapshotting' // re-fetching the queue for a fresh eligibility snapshot
  | 'confirm' // the pre-run confirmation is on screen
  | 'active' // initiating or waiting on the current invoice
  | 'completed'
  | 'stopped'
  | 'halted';

export type RunHaltKind = 'failed' | 'unknown' | 'dry_run' | 'initiate-error';

/**
 * Why a run halted (CHO-762 rule 4 — anything that is not a clean success).
 * `attempt` is the service-reported terminal attempt for `failed`/`unknown`/
 * `dry_run`; `initiate-error` means the initiation request itself was
 * rejected and `error` carries the message.
 */
export interface RunHalt {
  kind: RunHaltKind;
  invoice: PayableInvoice;
  attempt: PaymentAttempt | null;
  error: string | null;
}

export interface PaymentRun {
  status: PaymentRunStatus;
  /** Eligible invoices in due-date order — snapshotted at confirm time, never re-derived mid-run (rule 9). */
  runSet: PayableInvoice[];
  /** Ineligible invoices at snapshot time — listed in the confirmation, never silently dropped. */
  skipped: PayableInvoice[];
  currentIndex: number;
  successes: RunSuccess[];
  /** Running total spent, per currency — never summed across currencies. */
  totals: Record<string, number>;
  /** True while the initiation request for the current invoice is in flight. */
  initiating: boolean;
  /** Feeds the internal poller; null stops polling (terminal reached or stopped). */
  attemptId: string | null;
  /** Seeded from the invoice on initiation, until the poller's first sweep replaces it. */
  attempt: PaymentAttempt | null;
  /** What the wait panel shows: the polled snapshot when present, else the seed. */
  latestAttempt: PaymentAttempt | null;
  pollError: string | null;
  waitingStartedAt: number;
  halt: RunHalt | null;
  /** Set when stopped with an attempt still live — the approval may still complete it. */
  stoppedInFlight: { invoice: PayableInvoice; attemptId: string } | null;
  /** A failed snapshot (or a snapshot with nothing eligible) surfaces here, back on the queue view. */
  confirmError: string | null;
  beginSnapshot: () => void;
  confirmRun: () => void;
  cancelConfirm: () => void;
  stopRun: () => void;
  reset: () => void;
}

function message(err: unknown, prefix: string): string {
  return err instanceof Error ? `${prefix} (${err.message})` : prefix;
}

/**
 * The "pay all pending" run (CHO-762): a continuous pass over the eligible
 * queue. Each invoice is still initiated individually through the existing
 * single-payment call, still waits on the operator's per-payment 2FA approval
 * via the shared attempt poller, and still records exactly what the service
 * reports. The ONLY automation is sequencing: on a confirmed success the next
 * initiation starts by itself; anything that is not a clean success halts the
 * run for a human. The service keeps re-checking eligibility on every POST —
 * the UI never re-derives payability and never retries.
 *
 * All state is client-side React state; a page reload mid-run simply loses the
 * run (any in-flight attempt is unaffected server-side, rule 12).
 */
export function usePaymentRun(): PaymentRun {
  const [status, setStatus] = useState<PaymentRunStatus>('idle');
  const [runSet, setRunSet] = useState<PayableInvoice[]>([]);
  const [skipped, setSkipped] = useState<PayableInvoice[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [successes, setSuccesses] = useState<RunSuccess[]>([]);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [initiating, setInitiating] = useState(false);
  const [attempt, setAttempt] = useState<PaymentAttempt | null>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [waitingStartedAt, setWaitingStartedAt] = useState(0);
  const [halt, setHalt] = useState<RunHalt | null>(null);
  const [stoppedInFlight, setStoppedInFlight] = useState<{ invoice: PayableInvoice; attemptId: string } | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  // Bumped by stop/reset: an initiation started under an older generation
  // must not advance the run (or resurrect a stopped one) when it settles.
  const generation = useRef(0);

  const { attempt: polled, pollError } = useAttemptPoller(status === 'active' ? attemptId : null);
  const latestAttempt = polled ?? attempt;

  const initiateAt = useCallback(async (index: number, set: PayableInvoice[], gen: number) => {
    const invoice = set[index];
    if (!invoice) return;
    setInitiating(true);
    try {
      const started = await api.initiatePayment(invoice.id);
      if (generation.current !== gen) {
        // Stopped while the initiation was in flight — the attempt itself may
        // well exist server-side now; the stopped summary must say so.
        setStoppedInFlight({ invoice, attemptId: started.attemptId });
        return;
      }
      // The service answers 202 { attemptId, status } — an acknowledgement, not
      // an attempt row. Seed from the invoice so the wait panel has something
      // to show; the poller replaces it with the real row on its first sweep.
      setAttemptId(started.attemptId);
      setAttempt({
        id: started.attemptId,
        invoiceId: invoice.id,
        amount: invoice.amount,
        currency: invoice.currency,
        cardLast4: '',
        status: started.status,
        failureReason: null,
        createdAt: new Date().toISOString(),
        resolvedAt: null,
        resolvedBy: null,
        resolvedNote: null,
      });
      setWaitingStartedAt(Date.now());
    } catch (err) {
      if (generation.current !== gen) return;
      setHalt({ kind: 'initiate-error', invoice, attempt: null, error: message(err, 'Could not start the payment') });
      setStatus('halted');
    } finally {
      if (generation.current === gen) setInitiating(false);
    }
  }, []);

  // Terminal transition for the current invoice, driven by the poller's server
  // truth (never by the seeded attempt). 'succeeded' ONLY records the success
  // and advances; failed/unknown/dry_run all halt the run for a human.
  useEffect(() => {
    if (status !== 'active' || initiating) return;
    if (!polled || polled.status === 'initiated') return;
    const invoice = runSet[currentIndex];
    if (!invoice) return;

    if (polled.status === 'succeeded') {
      setSuccesses([...successes, { invoice, attemptId: polled.id, amount: polled.amount, currency: polled.currency }]);
      setTotals({ ...totals, [polled.currency]: (totals[polled.currency] ?? 0) + polled.amount });
      if (currentIndex + 1 >= runSet.length) {
        setAttemptId(null);
        setStatus('completed');
      } else {
        setCurrentIndex(currentIndex + 1);
        void initiateAt(currentIndex + 1, runSet, generation.current);
      }
      return;
    }

    setAttemptId(null);
    setHalt({ kind: polled.status, invoice, attempt: polled, error: null });
    setStatus('halted');
  }, [status, initiating, polled, runSet, currentIndex, successes, totals, initiateAt]);

  const beginSnapshot = useCallback(() => {
    const gen = generation.current;
    setConfirmError(null);
    setStatus('snapshotting');
    void (async () => {
      try {
        const data = await api.getPaymentQueue();
        if (generation.current !== gen) return;
        const invoices = extractInvoices(data);
        const eligible = invoices
          .filter((invoice) => invoice.eligibility.payable)
          .sort((a, b) => dueDateSortKey(a).localeCompare(dueDateSortKey(b)));
        if (eligible.length === 0) {
          setConfirmError('The freshly loaded queue has nothing eligible to pay.');
          setStatus('idle');
          return;
        }
        setRunSet(eligible);
        setSkipped(invoices.filter((invoice) => !invoice.eligibility.payable));
        setStatus('confirm');
      } catch (err) {
        if (generation.current !== gen) return;
        setConfirmError(message(err, 'Could not load the queue for a pay-all run'));
        setStatus('idle');
      }
    })();
  }, []);

  const confirmRun = useCallback(() => {
    if (status !== 'confirm' || runSet.length === 0) return;
    setCurrentIndex(0);
    setSuccesses([]);
    setTotals({});
    setHalt(null);
    setStoppedInFlight(null);
    setStatus('active');
    void initiateAt(0, runSet, generation.current);
  }, [status, runSet, initiateAt]);

  const cancelConfirm = useCallback(() => {
    if (status === 'confirm') setStatus('idle');
  }, [status]);

  const stopRun = useCallback(() => {
    generation.current += 1;
    if (status === 'snapshotting' || status === 'confirm') {
      setStatus('idle');
      return;
    }
    if (status !== 'active') return;
    // Mid-wait: the attempt is real and cannot be recalled — the stopped
    // summary warns that a later phone approval completes it server-side.
    // Mid-initiation: no attemptId yet; if the POST lands, initiateAt's
    // generation check records it as stopped-in-flight.
    if (attemptId) setStoppedInFlight({ invoice: runSet[currentIndex], attemptId });
    setAttemptId(null);
    setStatus('stopped');
  }, [status, attemptId, runSet, currentIndex]);

  const reset = useCallback(() => {
    generation.current += 1;
    setStatus('idle');
    setRunSet([]);
    setSkipped([]);
    setCurrentIndex(0);
    setSuccesses([]);
    setTotals({});
    setInitiating(false);
    setAttempt(null);
    setAttemptId(null);
    setHalt(null);
    setStoppedInFlight(null);
    setConfirmError(null);
  }, []);

  return {
    status,
    runSet,
    skipped,
    currentIndex,
    successes,
    totals,
    initiating,
    attemptId,
    attempt,
    latestAttempt,
    pollError,
    waitingStartedAt,
    halt,
    stoppedInFlight,
    confirmError,
    beginSnapshot,
    confirmRun,
    cancelConfirm,
    stopRun,
    reset,
  };
}
